import { useState } from 'react'
import type { StructuredReport, StructuredReportStep } from '../types'
import {
  DecisionDetail,
  ExtractDetail,
  RecoveryDetail,
  StepIcon,
} from './StructuredRunReportDetails'

interface ChipSpec {
  label: string
  color: string
  hasBody: boolean
}

function chipSpec(step: LegacyStructuredReportStep): ChipSpec | null {
  if (step.recovery) {
    const failed = step.result === 'fail'
    return {
      label: failed ? 'failed' : 'recovered',
      color: failed
        ? 'bg-red-100 text-red-800'
        : 'bg-yellow-100 text-yellow-800',
      hasBody: true,
    }
  }
  if (step.type === 'decision') {
    return {
      label: 'decision',
      color: 'bg-blue-100 text-blue-800',
      hasBody: true,
    }
  }
  if (step.type === 'extract') {
    return {
      label: 'extract',
      color: 'bg-purple-100 text-purple-800',
      hasBody: true,
    }
  }
  return null
}

type LegacyStructuredReportStep = Omit<
  StructuredReportStep,
  'id' | 'number'
> & {
  id?: unknown
  number?: unknown
  name?: unknown
}

function stepIdentifier(step: LegacyStructuredReportStep): string | null {
  if (typeof step.id === 'string' && step.id) return step.id
  const legacyName = step.name
  if (typeof legacyName === 'string' && legacyName) return legacyName
  return null
}

function StepRow({
  step,
  index,
}: {
  step: LegacyStructuredReportStep
  index: number
}) {
  const [expanded, setExpanded] = useState(false)
  const chip = chipSpec(step)
  const identifier = stepIdentifier(step)
  if (!identifier) {
    return (
      <div className="py-0.5 text-sm text-red-700" role="alert">
        Unsupported persisted step data at position {index + 1}
      </div>
    )
  }
  const displayStep = step as StructuredReportStep

  return (
    <div className="py-0.5">
      <div className="flex items-center gap-2 text-sm">
        <StepIcon step={displayStep} />
        <span className="text-gray-400 font-mono w-5 text-right shrink-0">
          {typeof step.number === 'number' ? step.number : index + 1}.
        </span>
        <span className="text-gray-800">
          {identifier.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())}
        </span>
        {chip &&
          (chip.hasBody ? (
            <button
              onClick={() => setExpanded((v) => !v)}
              className={`text-xs px-1.5 py-0.5 rounded cursor-pointer hover:opacity-80 ${chip.color}`}>
              {chip.label} {expanded ? '▴' : '▾'}
            </button>
          ) : (
            <span className={`text-xs px-1.5 py-0.5 rounded ${chip.color}`}>
              {chip.label}
            </span>
          ))}
      </div>

      {step.postcondition && (
        <p className="ml-12 text-xs text-gray-500">{step.postcondition}</p>
      )}

      {expanded && (
        <div className="ml-12">
          {step.recovery && <RecoveryDetail step={displayStep} />}
          {step.decision && <DecisionDetail step={displayStep} />}
          {step.extract && <ExtractDetail step={displayStep} />}
        </div>
      )}
    </div>
  )
}

export function StructuredRunReport({ report }: { report: StructuredReport }) {
  if (!report?.steps?.length) return null
  const steps = report.steps as LegacyStructuredReportStep[]
  const identifiers = steps.map(stepIdentifier)
  const duplicateIdentifiers = new Set(
    identifiers.filter(
      (identifier): identifier is string =>
        identifier !== null &&
        identifiers.indexOf(identifier) !== identifiers.lastIndexOf(identifier)
    )
  )
  const invalidStepCount = identifiers.filter(
    (identifier) => identifier === null
  ).length

  return (
    <div className="max-w-2xl space-y-0.5">
      {invalidStepCount > 0 && (
        <div className="py-0.5 text-sm text-red-700" role="alert">
          Unsupported persisted step data ({invalidStepCount})
        </div>
      )}
      {[...duplicateIdentifiers].map((identifier) => (
        <div
          key={`duplicate-${identifier}`}
          className="py-0.5 text-sm text-red-700"
          role="alert">
          Duplicate persisted step identifier: {identifier}
        </div>
      ))}
      {steps.map((step, index) => {
        const identifier = identifiers[index]
        if (!identifier || duplicateIdentifiers.has(identifier)) return null
        return <StepRow key={identifier} step={step} index={index} />
      })}
    </div>
  )
}
